import Immutable from 'immutable';
import { call, put } from 'redux-saga/effects';
import qsParser from 'querystring';
import moment from 'moment';
import { get, getPathWithQueryString, pageContext } from 'ctm-util-functions';

import { validateAllPreviousPages } from './validate_all_previous_pages';
import { runApiOnMountForPage } from './run_api_on_mount_for_page';
import { runApiOnMountForEachPreviousPage } from './run_api_on_mount_for_each_previous_page';
import { handleCustomizeUrlQueries } from './handle_customize_url_queries';
import { handleLoadJourney } from './handle_load_journey';

const dynamicDefaults = {
    today: () => {
        return moment().tz('Australia/Brisbane').format('YYYY-MM-DD').toString();
    },
    mock: () => {
        // used for unit tests
        return 'mock';
    }
};

/**
 * Add any dynamic defaults to the preloadSchema
 * @param {Immutable.Map} preloadSchema - The preload schema being used for this request
 * @param {String} stateKey - The form stateKey
 * @returns {Immutable.Map} - The preload schema with dynamic defaults set
 */
const addDynamicDefaults = (preloadSchema, stateKey = 'vertical.quote') => {
    const formSchemaModel = pageContext.getProps().models.formSchemaModel;
    const dynamicDefaultValues = formSchemaModel.properties[stateKey].dynamicDefaults || {};

    for (const identifier of Object.keys(dynamicDefaultValues)) {
        // FOR NOW: ONLY MAKE USE OF THIS FOR FORM ELEMENTS THAT SET THEIR OWN DEFAULTS WHEN MOUNTED
        // WE CAN'T CHECK FOR PRELOAD ON THE SERVER BEFORE THIS RUNS, SO WE CAN ONLY SET DEFAULTS THAT WILL
        // MATCH THE COMPONENT DEFAULTS
        // @todo: investigate if there's a better way to do this / delay construction of models until after the route is known?
        const dynamicFuncName = dynamicDefaultValues[identifier];
        const dynamicFunc = dynamicDefaults[dynamicFuncName];
        if (!dynamicFunc) {
            throw Error(`Dynamic default: ${dynamicFuncName} for id: ${identifier} could not be found`);
        }
        // eslint-disable-next-line no-param-reassign
        preloadSchema = preloadSchema.setIn([...identifier.split('.')], dynamicFunc());
    }
    return preloadSchema;
};

/**
 * Load data via the onMount action (if specified in schema).
 * Also pull in any preload data (if specified in the URL).
 *
 * @param {Object} action - Redux action.
 * @param {Object} action.pageToLoad - The current page we are loading.
 * @param {Object} action.pageToLoad.api - All API schemas for the page.
 * @param {Object} action.pageToLoad.api.onMount - API schema to run for the onMount of the page.
 * @param {Object} action.location.query - The URL query object.
 * @param {String} action.location.query.preload - The preload value requested by the user.
 * @param {Boolean} action.location.query.load_journey - Flag to load existing journey data.
 * @throws {Error} Throw an Error if the preload schema is specified but cannot be found.
 */
export const loadPageData = function* ({ pageToLoad, location }) {
    // Exit early if passed bad data
    if (!pageToLoad || !location) {
        return;
    }

    const pageProps = pageContext.getProps();
    const { isSSR, preloadSchemas = {} } = pageProps;

    // Extract the data we need.
    const pageState = get(pageContext.getProps(), 'pageState', {});
    const query = location.search ? qsParser.parse(location.search.substr(1)) : {};
    const journeyModel = get(pageProps, 'models.pageSchemaModel', {});
    const preloadSchemaName = query.preload || '';
    let preloadSchema = preloadSchemas[preloadSchemaName];
    const isLoadJourney = query.load_journey === 'true';

    const hasCustomizeQueries = Object.keys(query).length > 0 && !query.preload;

    // Business logic (order is important).
    if (isSSR) {
        if (preloadSchema) {
            // Update store with preload data (done server-side as the store is passed to the client).
            // eslint-disable-next-line no-param-reassign
            preloadSchema = addDynamicDefaults(Immutable.fromJS(preloadSchema));
            yield put({ type: 'PRELOAD_VALUES', preloadSchema, stateKey: 'vertical.quote' });
        }
    } else if (isLoadJourney && query.expired_email_token === 'true') {
        // Redirect to the start page, if email token is expired, e.g > 90 days
        yield call(_redirectToJourneyStart, pageState, journeyModel);
        // Dispatch expiredEmailTokenAction to display error messaging to user
        const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
        // @todo: check this, we may need to throw an error if settings.expiredEmailTokenAction is null, we can't put a null a action
        const expiredEmailTokenAction = yield call(get, settings, 'expiredEmailTokenAction', null);
        yield put(expiredEmailTokenAction);
    } else if (isLoadJourney && !query.journey_id && !query.transaction_id) {
        // Redirect to the start page, if no valid journey_id (load from everest) or transaction_id (load from legacy web_ctm email).
        yield call(_redirectToJourneyStart, pageState, journeyModel);
    } else if (isLoadJourney) {
        yield call(handleLoadJourney, pageToLoad, pageState, location);
    } else if (preloadSchemaName) {
        yield call(_handlePreload,
            pageToLoad,
            pageState,
            journeyModel,
            location.search);
    } else {
        if (hasCustomizeQueries) {
            yield call(handleCustomizeUrlQueries, query, pageState, journeyModel, location.search);
        }
        // Run the API onMount action for the current page (done client-side as it is async).
        yield call(runApiOnMountForPage, pageToLoad, pageState);
    }
};

/**
 * Generator to handle loading journey data from preload schemas
 * @param {Object} pageToLoad - The current page we are loading.
 * @param {Object} pageState - The page state
 * @param {Object} journeyModel - journeyModel instance so we can get first page schema
 * @param {String} searchStr - The complete, unparsed query string - eg '?preload=elec.json'
 * @private
 */
export const _handlePreload = function* (pageToLoad, pageState, journeyModel, searchStr) {
    // Dispatch an action to show the pleaseWaitOverlay while loading the page data.
    yield put({ type: 'SET_PAGE_VALUE', identifier: 'showPleaseWait', value: true });

    // Dispatch an action to set hideHighExcess data.
    yield put({ type: 'SET_PAGE_VALUE', identifier: 'isHideHighExcess', value: false });

    // Validate all previous pages, detect the first page with an error and make that the page to load.
    const firstPageWithError = yield call(validateAllPreviousPages, journeyModel, pageToLoad);
    const actualPageToLoad = firstPageWithError || pageToLoad;

    // Call the API onMount's for all step pages upto the page we are loading.
    yield call(runApiOnMountForEachPreviousPage, journeyModel, actualPageToLoad.name, pageState);
    yield call(runApiOnMountForPage, actualPageToLoad, pageState);

    // Reset transition state to avoid special page states (e.g please wait overlay).
    yield put({ type: 'SET_PAGE_VALUE', identifier: 'transitioningToNewPage', value: false });

    // Run API actions for each preloaded element that needs it (e.g postcode).
    yield put({ type: 'PRELOAD_FORM_ELEMENTS_API_VALUES', pageState });

    // Navigate user to the page with errors (if any).
    if (firstPageWithError) {
        const browserHistory = pageContext.getProps().browserHistory;
        yield call(browserHistory.replace,
            getPathWithQueryString(firstPageWithError.path, searchStr));
    }

    // Remove the pleaseWaitOverlay after loading the page data.
    yield put({ type: 'SET_PAGE_VALUE', identifier: 'showPleaseWait', value: false });
};

/**
 * Generator to redirect to journey start and run the onMount api on that page, NO SSR
 * @param {Object} pageState - Tightly coupled to PageWrapper mapStateToProps(), it has all page settings.
 * @param {Object} journeyModel - journeyModel instance so we can get first page schema
 * @private
 */
export const _redirectToJourneyStart = function* (pageState, journeyModel) {
    const firstPage = journeyModel.getFirstStepPage();
    const browserHistory = pageContext.getProps().browserHistory;
    yield call(browserHistory.replace, firstPage.path);
    yield call(runApiOnMountForPage, firstPage, pageState);
};
